import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import api from '../../../../../helpers/apiRequester'
import styles from './styles.module.scss'
import { Report } from 'notiflix/build/notiflix-report-aio'
import Notiflix from 'notiflix'
import { useAuthUserData } from '../../../../../authUserProvider'
import {
    ClientData,
    isValidEmail,
    isValidUKPhone,
    formatDate,
    getFirstName,
    getLastName,
    getFullName,
    getFormattedAddress,
    performClientSearch,
} from '../shared'

const BASE_URL =
    process.env.REACT_APP_API_URL === 'http://localhost:8080/api'
        ? 'http://localhost:8080'
        : 'https://case-delivery.sentinellegal.co.uk'

export const ReEngagementSearchView = () => {
    const navigate = useNavigate()
    const [clientData, setClientData] = useState<ClientData | null>(null)
    const [searchResults, setSearchResults] = useState<ClientData[]>([])
    const [searchTerm, setSearchTerm] = useState('')
    const [phoneNumber, setPhoneNumber] = useState('')
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [day, setDay] = useState('')
    const [month, setMonth] = useState('')
    const [year, setYear] = useState('')
    const [showDobFields, setShowDobFields] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [isSendingEmail, setIsSendingEmail] = useState(false)
    const [isUnsubscribing, setIsUnsubscribing] = useState(false)
    const [showClientSelection, setShowClientSelection] = useState(false)
    const [username, setUsername] = useState('')
    const authUser = useAuthUserData()

    useEffect(() => {
        if (authUser) {
            if (!username.length && authUser.name) {
                setUsername(authUser.name)
            }
        }
    }, [authUser, username.length])

    console.log('username: ', username)

    const onChange = (e: React.FormEvent<HTMLInputElement>) => {
        const newValue = e.currentTarget.value
        setSearchTerm(newValue)
    }

    const onPhoneChange = (e: React.FormEvent<HTMLInputElement>) => {
        const newValue = e.currentTarget.value
        setPhoneNumber(newValue)
    }

    const onFirstNameChange = (e: React.FormEvent<HTMLInputElement>) => {
        setFirstName(e.currentTarget.value)
    }

    const onLastNameChange = (e: React.FormEvent<HTMLInputElement>) => {
        setLastName(e.currentTarget.value)
    }

    const onDayChange = (e: React.FormEvent<HTMLSelectElement>) => {
        setDay(e.currentTarget.value)
    }

    const onMonthChange = (e: React.FormEvent<HTMLSelectElement>) => {
        setMonth(e.currentTarget.value)
    }

    const onYearChange = (e: React.FormEvent<HTMLSelectElement>) => {
        setYear(e.currentTarget.value)
    }

    const toggleDobFields = () => {
        setShowDobFields(!showDobFields)
        if (showDobFields) {
            // Clear DOB fields when hiding
            setDay('')
            setMonth('')
            setYear('')
        }
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        const trimmedFirstName = firstName.trim()
        const trimmedLastName = lastName.trim()
        const hasName = !!trimmedFirstName && !!trimmedLastName

        // Validate that email, phone number, or first and last name is provided
        if (!searchTerm.trim() && !phoneNumber.trim() && !trimmedFirstName && !trimmedLastName) {
            Notiflix.Report.failure(
                'Search Required',
                'Please provide an email address, phone number, or first and last name to search.',
                'OK'
            )
            return
        }

        // Require both names when searching by name
        if ((trimmedFirstName || trimmedLastName) && !hasName) {
            Notiflix.Report.failure(
                'Name Incomplete',
                'Both first and last name are required when searching by name.',
                'OK'
            )
            return
        }

        // Validate email if provided
        if (searchTerm.trim() && !isValidEmail(searchTerm)) {
            Notiflix.Report.failure('Invalid Email', 'Please check the email address and try again.', 'OK')
            return
        }

        // Validate phone number if provided
        if (phoneNumber.trim() && !isValidUKPhone(phoneNumber)) {
            Notiflix.Report.failure(
                'Invalid Phone Number',
                'Please enter a valid UK phone number with +44 country code (e.g., +447123456789).',
                'OK'
            )
            return
        }

        setIsLoading(true)
        setClientData(null)
        setSearchResults([])
        setShowClientSelection(false)

        try {
            // Format DOB if all fields are provided
            let dob = undefined
            if (day && month && year) {
                dob = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
            }

            const searchResult = await performClientSearch({
                email: searchTerm.trim() || undefined,
                phone: phoneNumber.trim() || undefined,
                firstName: trimmedFirstName || undefined,
                lastName: trimmedLastName || undefined,
                dob: dob,
            })

            if (!searchResult.success) {
                Notiflix.Report.failure('Search Error', 'An error occurred during the search. Please try again.', 'OK')
                return
            }

            console.log('searchResult: ', searchResult)

            if (searchResult.totalResults === 1) {
                // Single client found - display directly
                setClientData(searchResult.results[0])
            } else if (searchResult.totalResults > 1) {
                // Multiple clients found - show selection UI
                setSearchResults(searchResult.results)
                setShowClientSelection(true)
            } else {
                const notFoundError: any = new Error('Client not found')
                notFoundError.status = 404
                throw notFoundError
            }
        } catch (error: any) {
            // Check if the error is due to unauthorized access
            if (error?.response?.status === 401 || error?.status === 401) {
                Notiflix.Report.failure('Session Expired', 'Your session has expired. Please log in again.', 'OK')
                navigate('/login')
                return
            }

            if (error?.response?.status === 404 || error?.status === 404) {
                const searchType = searchTerm.trim() ? 'email address' : phoneNumber.trim() ? 'phone number' : 'name'
                Notiflix.Report.failure(
                    'Client Not Found',
                    `No client found with the provided ${searchType}. Please check the details and try again.`,
                    'OK'
                )
                return
            }

            Report.failure(
                'Search Error Occurred',
                'An error occurred whilst searching for your term. Please contact administrator.',
                'Okay'
            )
        } finally {
            setIsLoading(false)
        }
    }

    const handleClientSelection = (selectedClient: ClientData) => {
        setClientData(selectedClient)
        setShowClientSelection(false)
        setSearchResults([])
    }

    const handleSendLink = async (typeOfLink: 'email' | 'sms') => {
        if (!clientData?.esign_link) {
            Notiflix.Report.failure('No E-Sign Link', 'This client does not have an e-sign link available.', 'OK')
            return
        }

        const sendUrl = typeOfLink === 'email' ? 'send-esign-link' : 'send-esign-link-sms'

        setIsSendingEmail(true)
        console.log('clientData: ', clientData)
        try {
            await api({
                url: sendUrl,
                data: {
                    agent: authUser.name,
                    clientData: {
                        first_name: getFirstName(clientData),
                        last_name: getLastName(clientData),
                        email: clientData.email,
                        telephone: clientData.telephone,
                    },
                    esign_link: clientData.esign_link,
                },
            })

            Notiflix.Report.success(
                'E-Sign Link Sent',
                'The e-sign link has been successfully sent to the client.',
                'OK'
            )
        } catch (error: any) {
            // Check if the error is due to unauthorized access
            if (error?.response?.status === 401 || error?.status === 401) {
                Notiflix.Report.failure('Session Expired', 'Your session has expired. Please log in again.', 'OK')
                navigate('/login')
                return
            }

            Report.failure('Send Error', 'An error occurred whilst sending the e-sign link. Please try again.', 'OK')
        } finally {
            setIsSendingEmail(false)
        }
    }

    const handleSendEsignLink = async () => {
        handleSendLink('email')
    }

    const handleSendEsignLinkSms = async () => {
        handleSendLink('sms')
    }

    const handleUnsubscribeEmail = async () => {
        if (!clientData?.email) {
            Notiflix.Report.failure('No Email Address', 'This client does not have an email address available.', 'OK')
            return
        }

        setIsUnsubscribing(true)
        try {
            await axios.post(`${BASE_URL}/portal/redeem-token/unsubscribe`, {
                email: clientData.email,
            })

            Notiflix.Report.success('Email Unsubscribed', 'The email has been successfully unsubscribed.', 'OK')
        } catch (error: any) {
            Report.failure(
                'Unsubscribe Error',
                'An error occurred whilst unsubscribing the email. Please try again.',
                'OK'
            )
        } finally {
            setIsUnsubscribing(false)
        }
    }

    useEffect(() => {
        console.log('clientData: ', clientData)
    }, [clientData])

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2>Client Re-Engagement Search</h2>
                <p>
                    Search for client information using their email address, phone number, or first and last name to
                    review details and manage e-sign processes. You must provide an email, phone number, or first and
                    last name to search.
                </p>
            </div>

            <div className={styles.searchSection}>
                <form onSubmit={handleSubmit} className={styles.searchForm}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="phoneSearch">Client Phone Number</label>
                        <input
                            id="phoneSearch"
                            type="tel"
                            value={phoneNumber}
                            onChange={onPhoneChange}
                            placeholder="+44 (e.g., +447123456789)"
                        />
                        <small className={styles.fieldDescription}>
                            Enter the client's UK phone number with +44 country code
                        </small>
                    </div>

                    <div className={styles.orSeparator}>
                        <span>OR</span>
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="emailSearch">Client Email Address</label>
                        <input
                            id="emailSearch"
                            type="email"
                            value={searchTerm}
                            onChange={onChange}
                            placeholder="Enter client email address"
                        />
                        <small className={styles.fieldDescription}>Enter the client's email address</small>
                    </div>

                    <div className={styles.orSeparator}>
                        <span>OR</span>
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="firstNameSearch">Client Name</label>
                        <div className={styles.nameFieldsRow}>
                            <input
                                id="firstNameSearch"
                                type="text"
                                value={firstName}
                                onChange={onFirstNameChange}
                                placeholder="First name"
                            />
                            <input
                                id="lastNameSearch"
                                type="text"
                                value={lastName}
                                onChange={onLastNameChange}
                                placeholder="Last name"
                            />
                        </div>
                        <small className={styles.fieldDescription}>
                            Both first and last name are required when searching by name
                        </small>
                    </div>

                    <div className={styles.dobSection}>
                        <button type="button" onClick={toggleDobFields} className={styles.dobToggle}>
                            Advanced Search
                            <svg
                                className={`${styles.chevron} ${showDobFields ? styles.chevronDown : styles.chevronUp}`}
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M3 4.5L6 7.5L9 4.5"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                        <hr />

                        {showDobFields && (
                            <div className={styles.dobFields}>
                                <small className={styles.fieldDescription}>
                                    Use advanced search options to improve search accuracy
                                </small>

                                <p className={styles.advancedSearchTitle}>DOB Additional Matching</p>
                                <div className={styles.dobFieldsRow}>
                                    <div className={styles.dobFieldGroup}>
                                        <label htmlFor="daySelect">Day</label>
                                        <select id="daySelect" value={day} onChange={onDayChange}>
                                            <option value="">Day</option>
                                            {Array.from({ length: 31 }, (_, i) => (
                                                <option key={i + 1} value={i + 1}>
                                                    {i + 1}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className={styles.dobFieldGroup}>
                                        <label htmlFor="monthSelect">Month</label>
                                        <select id="monthSelect" value={month} onChange={onMonthChange}>
                                            <option value="">Month</option>
                                            <option value="1">January</option>
                                            <option value="2">February</option>
                                            <option value="3">March</option>
                                            <option value="4">April</option>
                                            <option value="5">May</option>
                                            <option value="6">June</option>
                                            <option value="7">July</option>
                                            <option value="8">August</option>
                                            <option value="9">September</option>
                                            <option value="10">October</option>
                                            <option value="11">November</option>
                                            <option value="12">December</option>
                                        </select>
                                    </div>

                                    <div className={styles.dobFieldGroup}>
                                        <label htmlFor="yearSelect">Year</label>
                                        <select id="yearSelect" value={year} onChange={onYearChange}>
                                            <option value="">Year</option>
                                            {Array.from({ length: 100 }, (_, i) => {
                                                const currentYear = new Date().getFullYear()
                                                const yearValue = currentYear - i
                                                return (
                                                    <option key={yearValue} value={yearValue}>
                                                        {yearValue}
                                                    </option>
                                                )
                                            })}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <button type="submit" disabled={isLoading} className={styles.searchButton}>
                        {isLoading ? 'Searching...' : 'Search Client'}
                    </button>
                </form>
            </div>

            {isLoading && (
                <div className={styles.loadingContainer}>
                    <div className={styles.loadingSpinner}></div>
                    <p>Searching for client information...</p>
                </div>
            )}

            {showClientSelection && searchResults.length > 0 && (
                <div className={styles.clientSelectionContainer}>
                    <div className={styles.clientSelectionHeader}>
                        <h3>Multiple Clients Found</h3>
                        <p>Please select the client you want to view:</p>
                    </div>
                    <div className={styles.clientSelectionGrid}>
                        {searchResults.map((client, index) => (
                            <div key={index} className={styles.clientSelectionCard}>
                                <div className={styles.clientSelectionInfo}>
                                    <h4>{getFullName(client)}</h4>
                                    <div className={styles.clientSelectionDetails}>
                                        <div className={styles.infoRow}>
                                            <span className={styles.label}>Date of Birth:</span>
                                            <span className={styles.value}>{formatDate(client.dob)}</span>
                                        </div>
                                        <div className={styles.infoRow}>
                                            <span className={styles.label}>Email:</span>
                                            <span className={styles.value}>{client.email}</span>
                                        </div>
                                        {client.telephone && (
                                            <div className={styles.infoRow}>
                                                <span className={styles.label}>Telephone:</span>
                                                <span className={styles.value}>{client.telephone}</span>
                                            </div>
                                        )}
                                        <div className={styles.infoRow}>
                                            <span className={styles.label}>Address:</span>
                                            <span className={styles.value}>
                                                {getFormattedAddress(client.current_address)}
                                            </span>
                                        </div>
                                        <div className={styles.infoRow}>
                                            <span className={styles.label}>CFA Signed:</span>
                                            <span
                                                className={`${styles.value} ${styles.status} ${
                                                    client.cfa_signed ? styles.success : styles.warning
                                                }`}
                                            >
                                                {client.cfa_signed}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleClientSelection(client)}
                                    className={styles.selectClientButton}
                                >
                                    Select This Client
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {clientData && (
                <div className={styles.clientDataContainer}>
                    <div className={styles.clientHeader}>
                        <h3>Client Information</h3>
                        <div className={styles.actionButtons}>
                            {clientData.email && (
                                <button
                                    onClick={handleUnsubscribeEmail}
                                    disabled={isUnsubscribing}
                                    className={styles.esignButton}
                                >
                                    {isUnsubscribing ? 'Unsubscribing...' : 'Unsubscribe Email'}
                                </button>
                            )}
                            {clientData.esign_link && (
                                <>
                                    <button
                                        onClick={handleSendEsignLink}
                                        disabled={isSendingEmail}
                                        className={styles.esignButton}
                                    >
                                        {isSendingEmail ? 'Sending...' : 'Send E-Sign Link (Email)'}
                                    </button>
                                    <button
                                        onClick={handleSendEsignLinkSms}
                                        disabled={isSendingEmail}
                                        className={styles.esignButton}
                                    >
                                        {isSendingEmail ? 'Sending...' : 'Send E-Sign Link (SMS)'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    <div className={styles.clientGrid}>
                        <div className={styles.clientInfo}>
                            <h4>Personal Details</h4>
                            <div className={styles.infoRow}>
                                <span className={styles.label}>Name:</span>
                                <span className={styles.value}>{getFullName(clientData)}</span>
                            </div>
                            <div className={styles.infoRow}>
                                <span className={styles.label}>Date of Birth:</span>
                                <span className={styles.value}>{formatDate(clientData.dob)}</span>
                            </div>
                            <div className={styles.infoRow}>
                                <span className={styles.label}>Email:</span>
                                <span className={styles.value}>{clientData.email}</span>
                            </div>
                            {clientData.telephone && (
                                <div className={styles.infoRow}>
                                    <span className={styles.label}>Telephone:</span>
                                    <span className={styles.value}>{clientData.telephone}</span>
                                </div>
                            )}
                        </div>

                        <div className={styles.clientInfo}>
                            <h4>CFA Status</h4>
                            <div className={styles.infoRow}>
                                <span className={styles.label}>Origin:</span>
                                <span className={styles.value}>{clientData.origin}</span>
                            </div>
                            <div className={styles.infoRow}>
                                <span className={styles.label}>CFA Signed:</span>
                                <span
                                    className={`${styles.value} ${styles.status} ${
                                        clientData.cfa_signed ? styles.success : styles.warning
                                    }`}
                                >
                                    {clientData.cfa_signed ? 'Yes' : 'No'}
                                </span>
                            </div>
                            {clientData.cfa_signed_date && (
                                <div className={styles.infoRow}>
                                    <span className={styles.label}>CFA Signed Date:</span>
                                    <span className={styles.value}>{formatDate(clientData.cfa_signed_date)}</span>
                                </div>
                            )}
                            {clientData.cfa_signed &&
                                clientData.claims &&
                                clientData.claims.length > 0 &&
                                clientData.claims[0].cfa_link && (
                                    <div className={styles.infoRow}>
                                        <span className={styles.label}>Signed Document:</span>
                                        <span className={styles.value}>
                                            <a
                                                href={clientData.claims[0].cfa_link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={styles.link}
                                            >
                                                View Signed CFA
                                            </a>
                                        </span>
                                    </div>
                                )}
                            {/* {clientData.cfa_signed !== 'YES' && clientData.esign_link && (
                                <div className={styles.infoRow}>
                                    <span className={styles.label}>E-Sign Link:</span>
                                    <span className={styles.value}>
                                        <a
                                            href={clientData.esign_link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={styles.link}
                                        >
                                            View E-Sign Link
                                        </a>
                                    </span>
                                </div>
                            )} */}
                            {/* {clientData.cfa_signed !== 'YES' && (
                                <div className={styles.infoRow}>
                                    <span className={styles.label}>Actions:</span>
                                    <span className={styles.value}>
                                        <>
                                            <button
                                                onClick={handleSendEsignLink}
                                                disabled={isSendingEmail}
                                                className={styles.actionButton}
                                            >
                                                {isSendingEmail ? 'Sending...' : 'Send email'}
                                            </button>
                                            <button
                                                onClick={handleSendEsignLinkSms}
                                                disabled={isSendingEmail}
                                                className={styles.actionButton}
                                            >
                                                {isSendingEmail ? 'Sending...' : 'Send SMS'}
                                            </button>
                                        </>
                                    </span>
                                </div>
                            )} */}
                            {clientData.has_blocked_claims && (
                                <div className={styles.infoRow}>
                                    <span className={styles.label}>Blocked Claims:</span>
                                    <span className={`${styles.value} ${styles.status} ${styles.error}`}>Yes</span>
                                </div>
                            )}
                        </div>

                        <div className={styles.clientInfo}>
                            <h4>Current Address</h4>
                            <div className={styles.addressInfo}>
                                {clientData.current_address.flat && (
                                    <div className={styles.addressLine}>{clientData.current_address.flat}</div>
                                )}
                                {clientData.current_address.house_number && (
                                    <div className={styles.addressLine}>
                                        {clientData.current_address.house_name &&
                                            `${clientData.current_address.house_name}, `}
                                        {clientData.current_address.house_number} {clientData.current_address.street}
                                    </div>
                                )}
                                {clientData.current_address.town_city && (
                                    <div className={styles.addressLine}>{clientData.current_address.town_city}</div>
                                )}
                                {clientData.current_address.county && (
                                    <div className={styles.addressLine}>{clientData.current_address.county}</div>
                                )}
                                {clientData.current_address.postcode && (
                                    <div className={styles.addressLine}>{clientData.current_address.postcode}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {clientData.claims && clientData.claims.length > 0 && (
                        <div className={styles.claimsSection}>
                            <h4>Claims ({clientData.claims.length})</h4>
                            <div className={styles.claimsGrid}>
                                {clientData.claims.map((claim, index) => (
                                    <div key={index} className={styles.claimCard}>
                                        <div className={styles.claimHeader}>
                                            <span className={styles.caseRef}>Case: {claim.case_ref}</span>
                                            <span
                                                className={`${styles.status} ${
                                                    claim.cancelled ? styles.error : styles.success
                                                }`}
                                            >
                                                {claim.cancelled ? 'Cancelled' : 'Active'}
                                            </span>
                                        </div>
                                        <div className={styles.claimDetails}>
                                            <div className={styles.infoRow}>
                                                <span className={styles.label}>Lender:</span>
                                                <span className={styles.value}>{claim.lender}</span>
                                            </div>
                                            <div className={styles.infoRow}>
                                                <span className={styles.label}>Policy Number:</span>
                                                <span className={styles.value}>
                                                    {claim.policy_number ||
                                                        'Claim discovery pending Esignature completion'}
                                                </span>
                                            </div>
                                            {claim.start_date && claim.policy_number && (
                                                <div className={styles.infoRow}>
                                                    <span className={styles.label}>Start Date:</span>
                                                    <span className={styles.value}>{formatDate(claim.start_date)}</span>
                                                </div>
                                            )}
                                            {claim.length_of_contract && (
                                                <div className={styles.infoRow}>
                                                    <span className={styles.label}>Contract Length:</span>
                                                    <span className={styles.value}>{claim.length_of_contract}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
